<template>
  <div style="display: flex; flex-direction: column; background: #F8F8F9;">
    <!-- Toolbar -->
    <div class="cm-toolbar">
      <h2>Daily Operations</h2>

      <!-- Date stepper -->
      <div class="cm-stepper">
        <button class="cm-icon-btn" @click="shiftRange(-1)"><FontAwesomeIcon :icon="['fas', 'chevron-left']" style="font-size: 10px;" /></button>
        <input class="cm-input cm-input-sm" type="date" v-model="dateFrom" @change="fetchData" style="width: 108px; text-align: center;" />
        <span style="color: #A5A5A6; font-size: 11px;">→</span>
        <input class="cm-input cm-input-sm" type="date" v-model="dateTo" @change="fetchData" style="width: 108px; text-align: center;" />
        <button class="cm-icon-btn" @click="shiftRange(1)" :disabled="isToday"><FontAwesomeIcon :icon="['fas', 'chevron-right']" style="font-size: 10px;" /></button>
      </div>

      <!-- Quick range -->
      <div class="cm-seg">
        <button
          v-for="preset in rangePresets"
          :key="preset.label"
          :class="{ on: isActivePreset(preset) }"
          @click="applyPreset(preset)"
        >{{ preset.label }}</button>
      </div>

      <span style="font-size: 11.5px; font-weight: 600;" :style="{ color: isToday ? '#1F9A63' : '#A5A5A6' }">
        {{ rangeLabel }}
      </span>

      <div style="width: 240px; margin-left: 12px;">
        <SimpleMultiSelect
          v-model="selectedTeamManagerIds"
          :options="teamOptions"
          placeholder="All teams"
          :searchable="true"
          @change="handleTeamFilterChange"
        />
      </div>

      <!-- Program filter -->
      <div style="margin-left: auto; display: flex; align-items: center; gap: 8px;">
        <div class="relative" ref="programDropdown">
          <button class="cm-btn cm-btn-secondary cm-btn-sm" @click="showProgramFilter = !showProgramFilter">
            <FontAwesomeIcon :icon="['fas', 'filter']" style="font-size: 10px;" />
            {{ programFilterLabel }}
          </button>
          <div
            v-if="showProgramFilter"
            class="absolute right-0 top-full mt-1 w-56 bg-white rounded-lg border z-30 py-1"
            style="border-color: #E8E9E9; box-shadow: 0 4px 16px rgba(38,38,40,0.10), 0 1px 3px rgba(38,38,40,0.06);"
          >
            <button
              @click="toggleAllPrograms"
              class="w-full px-3 py-1.5 text-left hover:bg-gray-50"
              style="font-size: 11.5px; font-weight: 500; color: #353537; border: none; background: transparent; cursor: pointer;"
            >{{ allProgramsSelected ? 'Deselect All' : 'Select All' }}</button>
            <div style="border-top: 1px solid #F0F0F1; margin: 4px 0;"></div>
            <div style="max-height: 280px; overflow-y: auto;">
              <label
                v-for="cat in ALL_CASE_CATEGORIES"
                :key="cat"
                class="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 cursor-pointer"
                style="font-size: 11.5px;"
              >
                <input type="checkbox" :checked="selectedPrograms.includes(cat)" @change="toggleProgram(cat)" />
                <span class="w-2 h-2 rounded-full shrink-0" :style="{ backgroundColor: CATEGORY_COLORS[cat] || '#6B7280' }"></span>
                <span style="color: #353537;">{{ SHORT_LABELS[cat] || cat }}</span>
              </label>
            </div>
          </div>
        </div>
        <button class="cm-icon-btn" @click="fetchData" :disabled="loading" :title="'Refresh'">
          <FontAwesomeIcon :icon="['fas', 'arrows-rotate']" :spin="loading" style="font-size: 11px;" />
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="cm-loading"><div class="cm-spinner"></div></div>

    <!-- Summary + Table -->
    <template v-else-if="hasData">
      <!-- Summary stat chips -->
      <div style="display: flex; align-items: center; gap: 8px; flex-wrap: wrap; padding: 14px 16px;">
        <div class="cm-stat"><span class="cm-stat-label">Roster</span><span class="cm-stat-value">{{ summaryRoster }}</span></div>
        <div class="cm-stat"><span class="cm-stat-label">Due Today</span><span class="cm-stat-value">{{ summaryHopper }}</span></div>
        <div class="cm-stat is-info" style="cursor: pointer; transition: transform 0.1s;" :style="selectedDrillDate ? { outline: '2px solid #2563EB', outlineOffset: '2px' } : {}" @click="toggleArrivalsDrill" title="Click to view opportunities created in this window"><span class="cm-stat-label">New (Created)</span><span class="cm-stat-value" style="text-decoration: underline;">{{ summaryNew }}</span></div>
        <div class="cm-stat is-ok"><span class="cm-stat-label">Actioned</span><span class="cm-stat-value">{{ summaryActioned }}</span></div>
        <div class="cm-stat"><span class="cm-stat-label">End of Day</span><span class="cm-stat-value">{{ summaryEnd }}</span></div>
        <div class="cm-stat is-brand"><span class="cm-stat-label">Completion</span><span class="cm-stat-value">{{ summaryRate }}%</span></div>
      </div>

      <!-- Matrix table -->
      <div style="padding: 0 16px 16px;">
        <div class="cm-panel" style="display: inline-block; min-width: 100%;">
          <table class="cm-table" style="border-collapse: separate; border-spacing: 0;">
            <thead>
              <!-- Row 1: Category group headers -->
              <tr>
                <th style="position: sticky; left: 0; z-index: 3; background: #F8F8F9; min-width: 156px;"></th>
                <th
                  v-for="cat in filteredCategories"
                  :key="'grp-' + cat.key"
                  :colspan="5"
                  class="ctr"
                  style="border-left: 2px solid #E8E9E9;"
                >
                  <span style="display: inline-flex; align-items: center; gap: 5px;">
                    <span style="width: 7px; height: 7px; border-radius: 50%;" :style="{ background: cat.color }"></span>
                    <span style="color: #353537;">{{ cat.label }}</span>
                  </span>
                </th>
              </tr>
              <!-- Row 2: Metric sub-headers -->
              <tr>
                <th style="position: sticky; left: 0; z-index: 3; background: #F8F8F9;">Care Manager</th>
                <template v-for="cat in filteredCategories" :key="'sub-' + cat.key">
                  <th class="num" :style="{ borderLeft: '2px solid #E8E9E9', paddingLeft: '6px', paddingRight: '6px' }" title="Roster"><span style="color: #8B8B8D; font-size: 10px;">R</span></th>
                  <th class="num" style="padding-left: 6px; padding-right: 6px;" title="Start of day"><span style="color: #8B8B8D; font-size: 10px;">S</span></th>
                  <th class="num" style="padding-left: 6px; padding-right: 6px;" title="New in"><span style="color: #2563EB; font-size: 10px;">+</span></th>
                  <th class="num" style="padding-left: 6px; padding-right: 6px;" title="Completed"><span style="color: #1F9A63; font-size: 10px;">✓</span></th>
                  <th class="num" style="padding-left: 6px; padding-right: 6px;" title="End of day"><span style="color: #8B8B8D; font-size: 10px;">E</span></th>
                </template>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in tableRows" :key="row.key">
                <td
                  v-if="row.type === 'team'"
                  :colspan="1 + filteredCategories.length * metrics.length"
                  class="sensitive"
                  style="background: #EEF4FA; border-bottom: 1px solid #D7E3F0; color: #294C70; font-size: 11.5px; font-weight: 700; letter-spacing: 0.01em; padding: 8px 12px;"
                >{{ teamHeaderLabel(row.managerName) }}</td>
                <template v-else>
                  <td style="position: sticky; left: 0; z-index: 1; border-bottom: 1px solid #F0F0F1;"
                    :style="{
                      background: row.cm === 'Unassigned' ? '#FDF4E7' : '#FFFFFF',
                    }">
                    <span style="display: inline-flex; align-items: center; gap: 8px;">
                      <span v-if="row.cm === 'Unassigned'" style="width: 22px; height: 22px; display: inline-flex; align-items: center; justify-content: center; color: #B45309;"><FontAwesomeIcon :icon="['fas', 'exclamation-triangle']" style="font-size: 11px;" /></span>
                      <span v-else class="cm-avatar" :style="avatarStyle(row.cm)">{{ avatarInitials(row.cm) }}</span>
                      <span class="sensitive" :style="{ fontWeight: 500, color: row.cm === 'Unassigned' ? '#B45309' : '#353537' }">{{ formatName(row.cm) }}</span>
                    </span>
                  </td>
                  <template v-for="cat in filteredCategories" :key="row.cm + '-' + cat.key">
                    <td v-for="(m, mi) in metrics" :key="row.cm + cat.key + m.k" class="num"
                      :style="{
                        borderLeft: mi === 0 ? '2px solid #E8E9E9' : undefined,
                        paddingLeft: '6px', paddingRight: '6px',
                        color: cellColor(cell(row.cm, cat.key, m.k), m.k),
                        fontWeight: (m.k === 'new' || m.k === 'done') ? 600 : 400,
                      }">{{ cell(row.cm, cat.key, m.k) || '' }}</td>
                  </template>
                </template>
              </tr>

              <!-- Totals row -->
              <tr>
                <td style="position: sticky; left: 0; z-index: 1; background: #F8F8F9; font-weight: 700; color: #353537; border-top: 2px solid #D8D8DA;">Total</td>
                <template v-for="cat in filteredCategories" :key="'tot-' + cat.key">
                  <td v-for="(m, mi) in metrics" :key="'tot' + cat.key + m.k" class="num"
                    :style="{
                      borderLeft: mi === 0 ? '2px solid #E8E9E9' : undefined,
                      paddingLeft: '6px', paddingRight: '6px',
                      background: '#F8F8F9', borderTop: '2px solid #D8D8DA', fontWeight: 700,
                      color: filteredTotal(cat.key, m.k) ? (m.k === 'new' ? '#2563EB' : m.k === 'done' ? '#1F9A63' : '#353537') : '#DBDBDC',
                    }">{{ filteredTotal(cat.key, m.k) || '' }}</td>
                </template>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Opportunity Arrivals Drilldown Panel -->
      <div v-if="drillActive" style="padding: 0 16px 24px;">
        <div class="cm-panel" style="padding: 16px; background: #FFFFFF;">
          <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; border-bottom: 1px solid #E8E9E9; padding-bottom: 8px;">
            <div>
              <span style="font-size: 13px; font-weight: 700; color: #353537;">Opportunities Created — {{ selectedDrillDate || dateFrom + ' to ' + dateTo }}</span>
              <span v-if="!drillLoading" style="font-size: 11.5px; color: #6B7280; margin-left: 8px;">({{ drillTotal }} opportunities)</span>
            </div>
            <button class="cm-icon-btn" @click="closeDrill" title="Close drilldown">
              <FontAwesomeIcon :icon="['fas', 'times']" style="font-size: 11px;" />
            </button>
          </div>

          <div v-if="drillLoading" class="cm-loading" style="padding: 24px 0;"><div class="cm-spinner"></div></div>
          <div v-else-if="!drillRows.length" style="padding: 24px 0; text-align: center; color: #8B8B8D; font-size: 12px;">
            No opportunities created in this selection
          </div>
          <div v-else style="overflow-x: auto; max-height: 480px; overflow-y: auto;">
            <table class="cm-table" style="width: 100%; font-size: 12px;">
              <thead>
                <tr>
                  <th style="text-align: left; position: sticky; top: 0; background: #F8F8F9; z-index: 2;">Patient</th>
                  <th style="text-align: left; position: sticky; top: 0; background: #F8F8F9; z-index: 2;">MBI</th>
                  <th style="text-align: left; position: sticky; top: 0; background: #F8F8F9; z-index: 2;">Program</th>
                  <th style="text-align: left; position: sticky; top: 0; background: #F8F8F9; z-index: 2;">Created At</th>
                  <th style="text-align: left; position: sticky; top: 0; background: #F8F8F9; z-index: 2;">Assigned Care Manager</th>
                  <th style="text-align: left; position: sticky; top: 0; background: #F8F8F9; z-index: 2;">Current Stage</th>
                  <th style="text-align: left; position: sticky; top: 0; background: #F8F8F9; z-index: 2;">Task Status</th>
                  <th style="text-align: left; position: sticky; top: 0; background: #F8F8F9; z-index: 2;">Last Action</th>
                  <th style="text-align: left; position: sticky; top: 0; background: #F8F8F9; z-index: 2;">Actioned By</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="opp in drillRows" :key="opp.opportunity_id" style="cursor: pointer;" @click="openPatientDrawer(opp)" class="cm-row-hover">
                  <td style="font-weight: 500; color: #2563EB;">{{ opp.patient_name || '—' }}</td>
                  <td class="sensitive" style="color: #6B7280;">{{ opp.patient_identifier || '—' }}</td>
                  <td>
                    <span class="cm-badge" style="background: #F0FDF4; color: #166534; border: 1px solid #BBF7D0;">
                      {{ opp.program_label || opp.case_category }}
                    </span>
                  </td>
                  <td style="color: #6B7280;">{{ formatTime(opp.created_at) }}</td>
                  <td>
                    <span :style="{ color: opp.owner_name === 'Unassigned' ? '#B45309' : '#353537', fontWeight: opp.owner_name === 'Unassigned' ? 600 : 400 }">
                      {{ opp.owner_name }}
                    </span>
                  </td>
                  <td style="color: #353537;">{{ opp.current_stage || '—' }}</td>
                  <td>
                    <span class="cm-badge" :style="statusBadgeStyle(opp.status)">
                      {{ opp.status || '—' }}
                    </span>
                  </td>
                  <td style="color: #4B5563;">{{ opp.last_action_name || '—' }}</td>
                  <td style="color: #6B7280;">{{ opp.last_actioned_by || '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </template>

    <!-- Empty -->
    <div v-else class="cm-empty">
      <FontAwesomeIcon :icon="['fas', 'table']" class="ico" />
      <div class="msg">No operations data for this date range</div>
    </div>

    <!-- Patient Drawer Slideover -->
    <div v-if="drawerOpen" class="fixed inset-0 z-50 flex justify-end bg-black/40" @click.self="closeDrawer">
      <div class="w-full max-w-4xl bg-white h-full shadow-2xl overflow-y-auto">
        <Patient_Drawer
          :patient-identifier="drawerPatientMbi"
          :entity-id="drawerEntityId"
          :patient-name="drawerPatientName"
          :enabled-tabs="['details', 'programs', 'engagement', 'care_graph', 'utilization']"
          :show-header="true"
          default-tab="programs"
          metric-category="Care Management"
          @close="closeDrawer"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import Patient_Drawer from '@/components/Patient_Drawer/Patient_Drawer.vue';
import SimpleMultiSelect from '@/components/widgetcomponents/Dropdown_Selector/SimpleMultiSelect.vue';
import {
  fetchCareManagementOperations,
  getCareManagementTeams,
} from '@/services/api/careManagementOperations';
import { mapGetters } from 'vuex';
import { z } from 'zod';
import abortable from '@/mixins/abortable';

const CATEGORY_COLORS = {
  'Post-Discharge IP': '#DC2626',
  'Post-Discharge ED': '#F59E0B',
  'SNF Admission': '#EC4899', '_total': '#353537',
};
const SHORT_LABELS = {
  'Post-Discharge IP': 'Post-Discharge IP',
  'Post-Discharge ED': 'Post-Discharge ED',
  'SNF Admission': 'SNF Admission', '_total': 'All Programs',
};
const ALL_CASE_CATEGORIES = [
  'Post-Discharge IP',
  'Post-Discharge ED',
  'SNF Admission',
];

const teamsSchema = z.array(z.object({
  manager_id: z.number(),
  manager_name: z.string(),
  members: z.array(z.object({ id: z.number(), name: z.string() })),
}));

export default {
  name: 'CareManagement_Operations_IHCI',
  components: { FontAwesomeIcon, Patient_Drawer, SimpleMultiSelect },
  mixins: [abortable],
  created() {
    this.ALL_CASE_CATEGORIES = ALL_CASE_CATEGORIES;
    this.CATEGORY_COLORS = CATEGORY_COLORS;
    this.SHORT_LABELS = SHORT_LABELS;
  },
  data() {
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    return {
      loading: false,
      trackerData: {},
      drillActive: false,
      drillLoading: false,
      drillRows: [],
      drillTotal: 0,
      selectedDrillDate: null,
      drawerOpen: false,
      drawerPatientMbi: null,
      drawerEntityId: null,
      drawerPatientName: '',
      dateFrom: today,
      dateTo: today,
      showProgramFilter: false,
      teams: [],
      selectedTeamManagerIds: [],
      metrics: [
        { k: 'roster' }, { k: 'start' }, { k: 'new' }, { k: 'done' }, { k: 'end' },
      ],
      selectedPrograms: (() => {
        try {
          const saved = localStorage.getItem('cm_ops_selected_programs_ihci');
          if (saved) { const parsed = JSON.parse(saved); if (Array.isArray(parsed) && parsed.length) return parsed; }
        } catch (e) { /* ignore */ }
        return [...ALL_CASE_CATEGORIES];
      })(),
    };
  },
  computed: {
    ...mapGetters('tenant', ['current_tenant_slug']),
    ...mapGetters('auth', ['current_user']),
    currentUserId() { return this.current_user?.id ?? null; },
    summaryRoster() { return this.summaryMetric('roster'); },
    summaryHopper() { return this.summaryMetric('start'); },
    summaryNew() { return this.summaryMetric('new'); },
    summaryActioned() { return this.summaryMetric('done'); },
    summaryEnd() { return this.summaryMetric('end'); },
    summaryRate() { return this.summaryHopper ? Math.round((this.summaryActioned / this.summaryHopper) * 100) : 0; },
    isToday() { const now = new Date(); return this.dateTo === `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`; },
    isSingleDay() { return this.dateFrom === this.dateTo; },
    rangeLabel() {
      if (this.isSingleDay) {
        if (this.isToday) return 'Today';
        const d = new Date(this.dateTo + 'T12:00:00');
        return `${['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][d.getDay()]} ${d.getMonth()+1}/${d.getDate()}`;
      }
      return `${Math.round((new Date(this.dateTo+'T12:00:00') - new Date(this.dateFrom+'T12:00:00')) / 86400000) + 1} days`;
    },
    rangePresets() {
      const now = new Date();
      const fmt = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const daysAgo = n => { const d = new Date(now); d.setDate(d.getDate() - n); return fmt(d); };
      return [ { label: 'Today', from: fmt(now), to: fmt(now) }, { label: '3d', from: daysAgo(2), to: fmt(now) }, { label: '7d', from: daysAgo(6), to: fmt(now) } ];
    },
    hasData() { return this.trackerData.care_managers?.length > 0; },
    isTeamFilterActive() { return this.selectedTeamManagerIds.length > 0; },
    teamOptions() {
      return this.teams.map(team => ({
        value: team.manager_id,
        label: this.teamDropdownLabel(team.manager_name, team.members.length),
      }));
    },
    tableRows() {
      const careManagers = this.trackerData.care_managers || [];
      if (!this.isTeamFilterActive) {
        return careManagers.map(cm => ({ type: 'careManager', cm, key: `cm-${cm}` }));
      }

      const rows = careManagers
        .filter(cm => cm === 'Unassigned')
        .map(cm => ({ type: 'careManager', cm, key: `cm-${cm}` }));
      const selectedIds = new Set(this.selectedTeamManagerIds);

      this.teams
        .filter(team => selectedIds.has(team.manager_id))
        .forEach(team => {
          const memberNames = new Set(team.members.map(member => member.name));
          rows.push({
            type: 'team',
            managerName: team.manager_name,
            key: `team-${team.manager_id}`,
          });
          careManagers
            .filter(cm => memberNames.has(cm))
            .forEach(cm => rows.push({
              type: 'careManager',
              cm,
              key: `team-${team.manager_id}-cm-${cm}`,
            }));
        });

      return rows;
    },
    displayedCareManagers() {
      return this.tableRows
        .filter(row => row.type === 'careManager')
        .map(row => row.cm);
    },
    allCategories() {
      const cats = (this.trackerData.case_categories || []).map(c => ({ key: c, label: SHORT_LABELS[c] || c, color: CATEGORY_COLORS[c] || '#6B7280' }));
      cats.push({ key: '_total', label: 'All Programs', color: '#353537' });
      return cats;
    },
    allowedCategories() { return new Set(this.selectedPrograms); },
    filteredCategories() { return this.allCategories.filter(c => c.key === '_total' || this.allowedCategories.has(c.key)); },
    allProgramsSelected() { return this.selectedPrograms.length === ALL_CASE_CATEGORIES.length; },
    programFilterLabel() {
      if (this.allProgramsSelected) return 'All Programs';
      if (this.selectedPrograms.length === 0) return 'No Programs';
      if (this.selectedPrograms.length === 1) return SHORT_LABELS[this.selectedPrograms[0]] || this.selectedPrograms[0];
      return `${this.selectedPrograms.length} Programs`;
    },
  },
  watch: {
    selectedPrograms: { handler(val) { localStorage.setItem('cm_ops_selected_programs_ihci', JSON.stringify(val)); }, deep: true },
    current_tenant_slug(newSlug, oldSlug) {
      if (newSlug !== oldSlug) this._reloadForContext();
    },
    currentUserId(newId, oldId) {
      if (newId !== oldId) this._reloadForContext();
    },
  },
  mounted() {
    this.fetchTeams();
    this.fetchData();
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeUnmount() { document.removeEventListener('click', this.handleClickOutside); },
  methods: {
    toggleArrivalsDrill() {
      if (this.drillActive) {
        this.closeDrill();
      } else {
        this.openArrivalsDrill(this.dateFrom);
      }
    },

    async openArrivalsDrill(targetDate) {
      this.drillActive = true;
      this.drillLoading = true;
      this.selectedDrillDate = targetDate;
      try {
        const payload = {
          view: 'arrivals_drilldown',
          date_from: this.dateFrom,
          date_to: this.dateTo,
          created_date: targetDate,
        };
        if (this.selectedTeamManagerIds.length) {
          payload.team_manager_ids = this.selectedTeamManagerIds;
        }
        const data = await fetchCareManagementOperations(payload);
        this.drillRows = data || [];
        this.drillTotal = this.drillRows.length;
      } catch (e) {
        this.drillRows = [];
        this.drillTotal = 0;
      } finally {
        this.drillLoading = false;
      }
    },

    closeDrill() {
      this.drillActive = false;
      this.selectedDrillDate = null;
      this.drillRows = [];
    },

    openPatientDrawer(opp) {
      this.drawerPatientMbi = opp.patient_identifier;
      this.drawerEntityId = opp.entity_id;
      this.drawerPatientName = opp.patient_name;
      this.drawerOpen = true;
    },

    closeDrawer() {
      this.drawerOpen = false;
      this.drawerPatientMbi = null;
      this.drawerEntityId = null;
    },

    formatTime(dtStr) {
      if (!dtStr) return '—';
      try {
        const d = new Date(dtStr);
        return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      } catch (e) {
        return dtStr;
      }
    },

    statusBadgeStyle(status) {
      if (status === 'Active') return { background: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE' };
      if (status === 'Closed Succeeded') return { background: '#F0FDF4', color: '#166534', border: '1px solid #BBF7D0' };
      if (status === 'Closed Out') return { background: '#F3F4F6', color: '#374151', border: '1px solid #E5E7EB' };
      return { background: '#F9FAFB', color: '#6B7280', border: '1px solid #E5E7EB' };
    },

    _reloadForContext() {
      this.trackerData = {};
      this.teams = [];
      this.selectedTeamManagerIds = [];
      this.fetchTeams();
      this.fetchData();
    },
    async fetchTeams() {
      const signal = this._renewAbortSignal('teams');
      const tenantSlug = this.current_tenant_slug;
      const userId = this.currentUserId;
      try {
        const data = await getCareManagementTeams({ signal });
        if (signal.aborted) return;
        if (this.current_tenant_slug !== tenantSlug || this.currentUserId !== userId) return;
        this.teams = teamsSchema.parse(data);
      } catch (e) {
        if (this._isAborted(e)) return;
        if (this.current_tenant_slug !== tenantSlug || this.currentUserId !== userId) return;
        console.error('Failed to fetch care management teams:', e);
        this.teams = [];
      }
    },
    handleTeamFilterChange() {
      this.trackerData = {};
      this.closeDrill();
      this.fetchData();
    },
    shiftRange(direction) {
      const from = new Date(this.dateFrom + 'T12:00:00'), to = new Date(this.dateTo + 'T12:00:00');
      const span = Math.round((to - from) / 86400000) + 1;
      from.setDate(from.getDate() + direction * span); to.setDate(to.getDate() + direction * span);
      const now = new Date(); now.setHours(23, 59, 59, 999);
      if (to > now) return;
      this.dateFrom = from.toISOString().split('T')[0]; this.dateTo = to.toISOString().split('T')[0];
      this.fetchData();
    },
    applyPreset(preset) { this.dateFrom = preset.from; this.dateTo = preset.to; this.fetchData(); },
    isActivePreset(preset) { return this.dateFrom === preset.from && this.dateTo === preset.to; },
    toggleProgram(key) { const idx = this.selectedPrograms.indexOf(key); if (idx >= 0) this.selectedPrograms.splice(idx, 1); else this.selectedPrograms.push(key); },
    toggleAllPrograms() { this.selectedPrograms = this.allProgramsSelected ? [] : [...ALL_CASE_CATEGORIES]; },
    handleClickOutside(e) { if (this.$refs.programDropdown && !this.$refs.programDropdown.contains(e.target)) this.showProgramFilter = false; },
    async fetchData() {
      const signal = this._renewAbortSignal('ops');
      const selectedTeamManagerIds = [...this.selectedTeamManagerIds];
      const tenantSlug = this.current_tenant_slug;
      const userId = this.currentUserId;
      this.loading = true;
      try {
        const body = { date_from: this.dateFrom, date_to: this.dateTo };
        if (selectedTeamManagerIds.length) body.team_manager_ids = selectedTeamManagerIds;
        const data = await fetchCareManagementOperations(body, { signal });
        if (signal.aborted) return;
        if (this.current_tenant_slug !== tenantSlug || this.currentUserId !== userId) return;
        if (this.selectedTeamManagerIds.join(',') !== selectedTeamManagerIds.join(',')) return;
        this.trackerData = data;
      } catch (e) {
        if (this._isAborted(e)) return;
        if (this.current_tenant_slug !== tenantSlug || this.currentUserId !== userId) return;
        if (this.selectedTeamManagerIds.join(',') !== selectedTeamManagerIds.join(',')) return;
        console.error('Failed to fetch daily tracker:', e);
        this.trackerData = {};
      } finally {
        if (!signal?.aborted) this.loading = false;
      }
    },
    cell(cm, cat, metric) {
      if (cat === '_total') { return this.filteredCategories.filter(c => c.key !== '_total').reduce((s, c) => s + (this.trackerData.data?.[cm]?.[c.key]?.[metric] || 0), 0) || ''; }
      const v = this.trackerData.data?.[cm]?.[cat]?.[metric] || 0;
      return v > 0 ? v : '';
    },
    filteredTotal(cat, metric) {
      if (cat === '_total') {
        return this.filteredCategories
          .filter(category => category.key !== '_total')
          .reduce((sum, category) => sum + this.filteredTotal(category.key, metric), 0) || '';
      }
      const v = this.isTeamFilterActive
        ? this.displayedCareManagers.reduce(
          (sum, cm) => sum + (this.trackerData.data?.[cm]?.[cat]?.[metric] || 0),
          0,
        )
        : this.trackerData.column_totals?.[cat]?.[metric] || 0;
      return v > 0 ? v : '';
    },
    summaryMetric(metric) {
      return this.filteredCategories
        .filter(category => category.key !== '_total')
        .reduce((sum, category) => sum + (this.filteredTotal(category.key, metric) || 0), 0);
    },
    teamDropdownLabel(managerName, memberCount) {
      return `${this.teamHeaderLabel(managerName)} (${memberCount})`;
    },
    teamHeaderLabel(managerName) {
      return `${this.formatName(managerName)}'s Team`;
    },
    cellColor(v, k) { return !v ? '#DBDBDC' : (k === 'new' ? '#2563EB' : k === 'done' ? '#1F9A63' : '#353537'); },
    formatName(name) {
      if (!name) return '—';
      if (name.includes(',')) { const [last, first] = name.split(',').map(s => s.trim()); return `${this.cap(first)} ${this.cap(last)}`; }
      return name;
    },
    cap(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : ''; },
    avatarInitials(name) {
      const parts = this.formatName(name).split(' ');
      return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase();
    },
    avatarStyle(name) {
      const hue = [...name].reduce((a, c) => a + c.charCodeAt(0), 0) % 360;
      return { color: `hsl(${hue} 42% 34%)`, background: `hsl(${hue} 44% 92%)` };
    },
  },
};
</script>
